import { ReactNode, useRef, useState } from 'react';
import {
  TooltipTrigger,
  OverlayArrow,
  Tooltip as AriaTooltip,
  Button as AriaButton,
} from 'react-aria-components';
import cx from 'classix';
import { Placement, useButton } from 'react-aria';
import { Icon } from '../atoms';
import { IconButton } from './Button';

type Props = {
  children: ReactNode;
  header?: string;
  supportingText?: string;
  /** Omit for auto placement */
  placement?: Placement;
  /** The Tooltips open/closed state must be externally controlled when set true in order for the tooltip to remain
   * open until the user closes it manually. */
  showCloseButton?: boolean;
  isOpen?: boolean;
  onCloseBtnPressed?: () => void;
  maxWidth?: string;
};

/** General-purpose Tooltip UI. Note that the children must consume React Aria providers for correct tooltip placement. */
export const Tooltip = ({
  children,
  header,
  supportingText,
  placement,
  showCloseButton,
  isOpen,
  onCloseBtnPressed,
  maxWidth,
}: Props) => {
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const { buttonProps } = useButton(
    {
      onPress: () => {
        setTooltipIsOpen(false);
        onCloseBtnPressed?.();
      },
    },
    ref,
  );

  return (
    <TooltipTrigger
      delay={0}
      closeDelay={0}
      isOpen={isOpen !== undefined ? isOpen : tooltipIsOpen}
      onOpenChange={setTooltipIsOpen}
    >
      {/* Note that this Tooltip auto places itself by depending on the child component consuming React Aria's
       * internal providers, and this tooltip will be misplaced and/or the overlay arrow will overlap with the
       * content if this is setup incorrectly. We cannot apply Tooltips to arbitrary content wihtout additional
       * work. If the tooltip is misformatted, please check that the child component is using React Aria hooks
       * and has the ref set correctly */}
      {children}
      <AriaTooltip
        offset={10}
        className={cx(
          'font-sans bg-gray-500 px-3 max-w-80 rounded-lg text-white shadow-lg',
          supportingText?.length ? 'py-3' : 'py-2',
        )}
        placement={placement}
      >
        <OverlayArrow
          className={({ placement }) =>
            cx(
              // Fix the container to be only half the size of the tooltip box so it's cropped into a triangle.
              (placement === 'left' || placement === 'right') && 'w-[6px] h-3',
              (placement === 'top' || placement === 'bottom') && 'w-3 h-[6px]',
              // Fit the arrow flush against the container for top and left tooltip placements
              placement === 'left' && '!left-[unset] right-0',
              placement === 'top' && '!top-[unset] bottom-0',
            )
          }
        >
          {/* This is a square that is drawn as the triangle tooltip arrow and then cropped into a triangle shape
           * by the container */}
          <div className='w-3 h-3 bg-gray-500 rotate-45' />
        </OverlayArrow>

        <div className={cx(maxWidth && `max-w-[${maxWidth}]`, 'flex flex-col gap-1')}>
          {header ||
            (showCloseButton && (
              <div className='flex items-center'>
                {header && <div className='text-xs font-semibold'>{header}</div>}
                {showCloseButton && (
                  <div
                    ref={ref}
                    {...buttonProps}
                    className='ml-auto p-1 mr-[-4px] [&>button]:text-white [&>button]:hover:text-white hover:cursor-pointer'
                  >
                    <Icon type='x-close' size='20' />
                  </div>
                )}
              </div>
            ))}
          {supportingText && <div className='text-xs'>{supportingText}</div>}
        </div>
      </AriaTooltip>
    </TooltipTrigger>
  );
};

/**
 * Renders an IconButton with the help-circle icon that opens a tooltip when clicked, and remains open until the user closes it
 * manually with a close button. */
export const HelpIcon = (
  props: Pick<Props, 'header' | 'supportingText' | 'placement' | 'maxWidth'> & {
    size?: 'xs' | 'sm' | 'md';
  },
) => {
  // TODO: Programmatically determine if using mobile resolution
  const isMobile = false;

  const [isOpen, setIsOpen] = useState(false);

  const size = props.size || 'sm';
  const sizeMap = {
    xs: '16',
    sm: '20',
    md: '24',
  };

  return (
    <Tooltip
      {...props}
      isOpen={isMobile ? isOpen : undefined}
      showCloseButton={isMobile}
      onCloseBtnPressed={() => setIsOpen(false)}
    >
      {
        (
          isMobile // currenty never used per above TODO
        ) ?
          <IconButton
            icon='help-circle'
            size={size != 'xs' ? size : 'sm'}
            variant='tertiary-gray'
            onPress={() => setIsOpen(true)}
          />
          // This is a dummy RAC button (not our DS component) to serve as the mouseover
          // target. There is a slight bug here where clicking the button causes
          // the Tooltip to disappear.
        : <AriaButton className='outline-none'>
            <Icon type='help-circle' stroke='gray-300' size={sizeMap[size]} />
          </AriaButton>

      }
    </Tooltip>
  );
};

/**
 * Renders an IconButton with the alert-circle icon that opens a tooltip when clicked, and remains open until the user closes it
 * manually with a close button. */
export const AlertIconTooltip = (
  props: Pick<Props, 'header' | 'supportingText' | 'placement'> & {
    size?: 'sm' | 'md';
  },
) => {
  // TODO: Programmatically determine if using mobile resolution
  const isMobile = false;

  const [isOpen, setIsOpen] = useState(false);

  const size = props.size || 'sm';

  return (
    <Tooltip
      {...props}
      isOpen={isMobile ? isOpen : undefined}
      showCloseButton={isMobile}
      onCloseBtnPressed={() => setIsOpen(false)}
    >
      {
        isMobile ?
          <IconButton
            icon='alert-circle'
            size={size}
            variant='tertiary-gray'
            onPress={() => setIsOpen(true)}
          />
          // This is a dummy RAC button (not our DS component) to serve as the mouseover
          // target. There is a slight bug here where clicking the button causes
          // the Tooltip to disappear.
        : <AriaButton className='outline-none'>
            <Icon type='alert-circle' stroke='current' size={size === 'sm' ? '20' : '24'} />
          </AriaButton>

      }
    </Tooltip>
  );
};
